import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaChartLine, FaFilter } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function SalesTrend() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  // Fetch product performance
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}getVendorProductPerformance/${vendorId}`);
        if (res.data.success) {
          setProducts(res.data.productPerformance || []);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Error fetching product performance:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) fetchPerformance();
  }, [vendorId]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter((p) =>
      `${p.name} ${p.sku}`.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [products, filters.search]);

  // Summary stats
  const totalRevenue = filteredProducts.reduce((acc, p) => acc + p.total_revenue, 0);
  const totalQuantity = filteredProducts.reduce((acc, p) => acc + p.quantity_sold, 0);

  // Chart data (horizontal bar for better readability)
  const chartData = useMemo(() => {
    return {
      labels: filteredProducts.map((p) =>
        p.name.length > 20 ? p.name.substring(0, 20) + "..." : p.name
      ),
      datasets: [
        {
          label: "Total Revenue (₹)",
          data: filteredProducts.map((p) => p.total_revenue),
          backgroundColor: "#f54c4d",
        },
        {
          label: "Quantity Sold",
          data: filteredProducts.map((p) => p.quantity_sold),
          backgroundColor: "rgba(54, 162, 235, 0.6)",
        },
      ],
    };
  }, [filteredProducts]);

  const chartOptions = {
    indexAxis: "y", // Horizontal bars
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Sales Trend Overview" },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const p = filteredProducts[tooltipItem.dataIndex];
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw} (${p.name})`;
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true },
      y: { ticks: { autoSkip: false } },
    },
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaChartLine /> Sales Trend
            </h4>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter /> Filters
            </button>
          </div>

          {/* Filters */}
          {showFilter && (
            <div className="card p-3 mb-3 shadow-sm border-0">
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label>Search Product</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Product name or SKU"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <button className="btn btn-primary w-100" onClick={() => setShowFilter(false)}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="d-flex gap-3 mb-3 flex-wrap">
            <div className="card p-3 flex-fill text-center shadow-sm border-0">
              <h6>Total Revenue</h6>
              <h4>₹{totalRevenue}</h4>
            </div>
            <div className="card p-3 flex-fill text-center shadow-sm border-0">
              <h6>Total Quantity Sold</h6>
              <h4>{totalQuantity}</h4>
            </div>
          </div>

          {/* Chart */}
          <div className="card shadow-sm border-0 p-3">
            {loading ? (
              <div className="text-center py-4 text-muted">Loading sales trend...</div>
            ) : filteredProducts.length > 0 ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <div className="text-center py-4 text-muted">No sales data available</div>
            )}
          </div>
        </div>
      </main>

      <style jsx>{`
        .card h6 {
          color: #555;
          font-weight: 500;
        }
        .card h4 {
          font-weight: 700;
          margin-top: 5px;
        }
      `}</style>
    </>
  );
}

export default SalesTrend;
